import { useCallback, useEffect, useRef, useState } from 'react'

import { ButtonDefault } from '@/components/common/Button'
import { FloatingBox } from '@/components/common/Floating'
import { IconAdvanced } from '@/components/icons/IconAdvanced'
import { useReduxSelector } from '@/store/hooks'
import { getClickawayIgnoreProps } from '@/utils/clickawayUtil'
import { cn } from '@/utils/commonUtil'

import { useGalleryPreload } from '../../GalleryPreloadProvider/GalleryPreloadProvider'
import { useGalleryBounce } from '../../hooks/useGalleryBounce'
import { GalleryCardFilters } from '../GalleryCardFilters'

import styles from './GalleryCardFiltersButton.module.scss'

const PANEL_ID = 'gallery-card-filters-panel'

export const GalleryCardFiltersButton = () => {
  const cardFilters = useReduxSelector((s) => s.gallery.cardFilters)
  const count = cardFilters?.length || 0
  const { startPreload } = useGalleryPreload()
  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const isBouncing = useGalleryBounce({ editedKeys: ['cardFilters'] })

  const onClickClose = useCallback(() => {
    setIsOpen(false)
    triggerRef.current?.focus()
  }, [setIsOpen])

  const onClick = useCallback(() => {
    setIsOpen((prev) => !prev)
  }, [setIsOpen])

  useEffect(() => {
    if (!isOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) onClickClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClickClose])

  return (
    <div
      {...getClickawayIgnoreProps(['collapse-expanded', 'card-filters'])}
      className={cn(styles.container, { isBouncing, styles })}
      onPointerEnter={startPreload}
    >
      <ButtonDefault
        aria-controls={isOpen ? PANEL_ID : undefined}
        aria-expanded={isOpen}
        aria-label='Card Filters'
        onClick={onClick}
        padding='none'
        ref={triggerRef}
        variant='cardFiltersButton'
      >
        <div className={styles.button}>
          <IconAdvanced.Root count={count} variant='cardFilters' />
        </div>
      </ButtonDefault>
      {isOpen && (
        <div className={styles.dropdown} id={PANEL_ID}>
          <div className={styles.arrow} />
          <FloatingBox
            borderColor='strong'
            content={<GalleryCardFilters onClickClose={onClickClose} variant='desktop' />}
            padding='none'
          />
        </div>
      )}
    </div>
  )
}
